
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { notificationsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useOnPageActive } from '@/hooks/useOnPageActive';
import { Notifications } from '@/types/client';

// Notifications come from crons and background jobs, so there is no poll: they are
// fetched when the user looks — on load, on returning to the tab, on changing page —
// and at most once per this window for those automatic triggers.
const STALE_AFTER_MS = 60_000;

interface NotificationContextType {
  actionableNotifications: Notifications[];
  otherNotifications: Notifications[];
  /** Fetch now — for explicit user actions (opening the bell, marking read). */
  refreshNotifications: () => void;
  /** Fetch only if the last fetch is older than a minute — for automatic triggers. */
  refreshIfStale: () => void;
  isLoading: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [actionableNotifications, setActionableNotifications] = useState<Notifications[]>([]);
  const [otherNotifications, setOtherNotifications] = useState<Notifications[]>([]);
  // Loading means "nothing fetched yet": true from the first render when there is
  // a user (so consumers show a loader, not "no notifications"), and never set
  // again by a later refresh — that made every consumer flicker to a loader.
  const [isLoading, setIsLoading] = useState(() => !!user?.id);
  const loadedForUserRef = useRef<number | string | null>(null);
  const lastFetchedAtRef = useRef(0);

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    lastFetchedAtRef.current = Date.now();
    if (loadedForUserRef.current !== user.id) setIsLoading(true);
    try {
      const data = await notificationsApi.listNotifications({
        user_id: user.id,
        days: 30,
        type: 'all',
      }, {});

      const allNotifications = data.results || [];

      const actionable = allNotifications.filter((n: Notifications) => n.is_actionable && !n.is_completed);
      const other = allNotifications.filter((n: Notifications) => !n.is_actionable && n.notification_type !== 'info' && !n.is_read);
      setActionableNotifications(actionable);
      setOtherNotifications(other);
      loadedForUserRef.current = user.id;
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const refreshNotifications = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const refreshIfStale = useCallback(() => {
    if (Date.now() - lastFetchedAtRef.current < STALE_AFTER_MS) return;
    fetchNotifications();
  }, [fetchNotifications]);

  useOnPageActive(refreshIfStale);

  return (
    <NotificationContext.Provider
      value={{
        actionableNotifications,
        otherNotifications,
        refreshNotifications,
        refreshIfStale,
        isLoading,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

/**
 * Refreshes notifications (if stale) when the user moves to another page. Rendered
 * inside the router: `NotificationProvider` sits above it, so it cannot read the route.
 */
export const NotificationRouteRefresher: React.FC = () => {
  const { pathname } = useLocation();
  const { refreshIfStale } = useNotification();
  const refreshIfStaleRef = useRef(refreshIfStale);
  refreshIfStaleRef.current = refreshIfStale;
  // The first page is covered by the provider's own load, so only a change counts.
  const previousPathnameRef = useRef(pathname);

  useEffect(() => {
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    refreshIfStaleRef.current();
  }, [pathname]);

  return null;
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};
